import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useRouteError, isRouteErrorResponse } from 'react-router';
import { Seo } from '../components/seo';
import { LocaleLink } from '../i18n/locale-link';
import { LocaleProvider, useT } from '../i18n/locale-context';
import { resolveLocaleFromPath } from '../i18n/index';
import { isPageTranslated, reportPageError } from '../lib/page-error';
import { isOfflineError } from '../lib/lazy-route';
import { useOnline } from '../lib/online-status';

/**
 * Re-provide the locale derived from the CURRENT URL to this subtree.
 *
 * Every other page reads the locale from the app-level <LocaleProvider>, which
 * <App> keeps in sync with the router. These two pages are the exception on
 * purpose: they also mount as an `errorElement`, i.e. exactly when the state
 * around them may be the thing that broke. The URL is this app's authoritative
 * locale signal (same rule <App> follows), so a dead end re-derives it locally
 * — that way the copy and the "back to the catalog" link can never disagree
 * with the address bar, and `/uz/<typo>` is Uzbek even if nothing else is.
 */
function UrlLocale({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <LocaleProvider locale={resolveLocaleFromPath(pathname)}>{children}</LocaleProvider>;
}

interface DeadEndProps {
  /** Small caps eyebrow above the heading — the status, at a glance. */
  badge: string;
  badgeClassName: string;
  title: string;
  body: string;
  /** Optional primary action rendered above the catalog link (error variant). */
  action?: ReactNode;
}

/**
 * Shared shell for the two dead ends below. Deliberately small — a heading, one
 * line of explanation, one way out — and built from the same vocabulary as the
 * other messaging states (`QueryError`, the tender "not found" card): centered
 * text, `font-brand` heading on `text-ink`, `text-ink-soft` body, and the
 * full-width-on-mobile brand button. Mobile-first: the button spans the screen
 * below `sm` and shrinks to its content at `sm`+.
 */
function DeadEnd({ badge, badgeClassName, title, body, action }: DeadEndProps) {
  const t = useT();
  return (
    <div className="mx-auto flex min-h-[46vh] max-w-[520px] flex-col items-center justify-center px-4 py-10 text-center">
      <span
        className={`mb-4 inline-flex h-[26px] items-center rounded-pill px-3 font-brand text-[12px] font-bold tracking-[0.06em] ${badgeClassName}`}
      >
        {badge}
      </span>
      <h1 className="mb-2 font-brand text-[22px] font-extrabold tracking-[-0.3px] text-ink sm:text-[26px]">
        {title}
      </h1>
      <p className="mb-6 text-[14px] leading-[1.7] text-ink-soft">{body}</p>
      <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
        {action}
        <LocaleLink
          to="/"
          className="inline-flex h-11 items-center justify-center rounded-md border border-edge bg-bone-surface px-5 text-[13px] font-bold text-ink transition-colors hover:bg-bone-hover"
        >
          {t('notFound.action')}
        </LocaleLink>
      </div>
    </div>
  );
}

function NotFoundBody() {
  const t = useT();
  return (
    <>
      {/* A dead end must never be indexed, and it has no canonical URL of its
          own to claim — so `noIndex` and no `canonical` (which also suppresses
          the hreflang/og:url block; there is no per-locale twin of a typo). */}
      <Seo title={t('notFound.title')} noIndex />
      <DeadEnd
        badge="404"
        badgeClassName="bg-brand-light text-brand"
        title={t('notFound.title')}
        body={t('notFound.body')}
      />
    </>
  );
}

/**
 * Catch-all page for a URL that matches no route — a mistyped address, or a
 * link to a page that no longer exists. Mounted as `path: '*'` inside BOTH
 * locale trees (see `publicChildren` in app.tsx), so it renders inside the
 * normal app shell with the sidebar still there to navigate from.
 */
export function NotFound() {
  return (
    <UrlLocale>
      <NotFoundBody />
    </UrlLocale>
  );
}

/** «Повторить» = a reload: a route that failed to load (a lazy chunk, which
 *  `React.lazy` caches as rejected) cannot be retried in place. */
function ReloadButton() {
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="inline-flex h-11 items-center justify-center rounded-md bg-brand px-5 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid"
    >
      {t('error.retry')}
    </button>
  );
}

function RouteErrorBody() {
  const t = useT();
  return (
    <>
      <Seo title={t('routeError.title')} noIndex />
      <DeadEnd
        badge={t('routeError.badge')}
        badgeClassName="bg-status-closing-bg text-status-closing-text"
        title={t('routeError.title')}
        body={t('routeError.body')}
        action={<ReloadButton />}
      />
    </>
  );
}

/**
 * The route failed because the browser has no connection (22.10). «Что-то
 * пошло не так» blames us and «обновите страницу» sends the person to a reload
 * that cannot work offline; this names the actual cause and the one action.
 */
function OfflineBody() {
  const t = useT();
  return (
    <>
      <Seo title={t('offline.title')} noIndex />
      <DeadEnd
        badge={t('offline.badge')}
        badgeClassName="bg-status-closing-bg text-status-closing-text"
        title={t('offline.title')}
        body={t('offline.body')}
        action={<ReloadButton />}
      />
    </>
  );
}

/**
 * `errorElement` for every top-level route — what renders when a route throws
 * during render (or a router-level response error) instead of react-router's
 * developer-facing default screen ("Unexpected Application Error!").
 *
 * A crash and a wrong URL get DIFFERENT copy on purpose: telling someone the
 * page does not exist when it does exist and merely failed would send them off
 * to fix an address that was fine. The one exception is a genuine 404 response
 * thrown into the boundary — that IS "not found", so it renders as such.
 *
 * Every crash it shows is reported (`page_error` + PostHog `$exception`, see
 * `lib/page-error.ts`): an error caught here never reaches `window.onerror`, so
 * without this the screen was invisible to every dashboard. A 404 is not a
 * crash and is not reported.
 *
 * Offline is not a crash either (22.10): an `OfflineError` from `lazyRoute`, or
 * any failure while the browser reports no connection, gets «Нет соединения с
 * интернетом» and is NOT reported — there is nothing of ours to fix, and no
 * network to send it over. If the connection comes back while a non-offline
 * error is on screen, it turns into the ordinary crash screen and is reported
 * then: it was a real failure after all.
 */
export function RouteError() {
  const error = useRouteError();
  const { pathname } = useLocation();
  const online = useOnline();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;
  const offline = !isNotFound && (isOfflineError(error) || !online);
  useReportCrash(error, pathname, !isNotFound && !offline);
  return (
    <UrlLocale>
      {isNotFound ? <NotFoundBody /> : offline ? <OfflineBody /> : <RouteErrorBody />}
    </UrlLocale>
  );
}

/**
 * Report the error this boundary caught, once per error.
 *
 * The "is the page machine-translated" check is taken during RENDER, not in the
 * effect: <UrlLocale> below rewrites `<html lang>` in its own effect, and child
 * effects run before this one — by then the language Chrome's translator left
 * on `<html>` would already be overwritten. `typeof document` keeps it
 * SSR/prerender-safe (the effect itself never runs there).
 *
 * The ref keeps a StrictMode double-invoked effect (dev) from reporting twice.
 */
function useReportCrash(error: unknown, pathname: string, enabled: boolean): void {
  const [translated] = useState(() =>
    isPageTranslated(
      typeof document === 'undefined' ? null : document,
      resolveLocaleFromPath(pathname),
    ),
  );
  const reported = useRef<{ error: unknown } | null>(null);
  useEffect(() => {
    if (!enabled || (reported.current !== null && reported.current.error === error)) return;
    reported.current = { error };
    reportPageError(error, pathname, translated);
  }, [enabled, error, pathname, translated]);
}
